import React, { useEffect, useState } from "react";
import { Container, Row, Col, Card, Spinner, Alert } from "react-bootstrap";
import { useHistory } from "react-router-dom";
import { useYear } from "../contexts/YearContext";
import { useEntity } from "../contexts/EntityContext";
import { useArea } from "../contexts/AreaContext";
import "./YearSelectionComponent.css";

const YearSelectionComponent: React.FC = () => {
    const history = useHistory();
    const { currentYear, availableYears, setCurrentYear, clearCurrentYear, loading: yearLoading, fetchAvailableYears } = useYear();
    const { currentEntity } = useEntity();
    const { currentArea } = useArea();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        clearCurrentYear();

        const initializeYears = async () => {
            try {
                setLoading(true);
                setError(null);

                if (currentEntity && currentArea) {
                    // Fetch years using the current entity and area
                    await fetchAvailableYears(currentEntity.entidade_id, currentArea.area_id);
                } else if (!currentEntity) {
                    setError('Nenhuma entidade selecionada');
                } else if (!currentArea) {
                    setError('Nenhuma área selecionada');
                }
            } catch (err) {
                setError('Erro ao carregar anos');
                console.error('Error loading years:', err);
            } finally {
                setLoading(false);
            }
        };

        initializeYears();
    }, [currentEntity, currentArea, fetchAvailableYears, clearCurrentYear]);

    useEffect(() => {
        if (!loading && !yearLoading && availableYears.length === 1 && !currentYear) {
            const singleYear = availableYears[0];
            handleYearSelect(singleYear);
        }
    }, [loading, yearLoading, availableYears, currentYear]);

    const handleYearSelect = async (year: any) => {
        try {
            if (!year || typeof year.ano_fiscal_id === 'undefined') {
                console.error('Invalid year object:', year);
                throw new Error('Invalid year object');
            }

            await setCurrentYear(year);

            if (currentArea) {
                const redirectPath = `/acoes/${currentArea.area_id}`;
                history.push(redirectPath);
            }
        } catch (err) {
            setError('Erro ao selecionar ano');
            console.error('Error selecting year:', err);
        }
    };

    const renderYearCard = (year: any) => (
        <Col md={6} key={year.ano_fiscal_id}>
            <Card
                className="entity-card"
                onClick={() => handleYearSelect(year)}
            >
                <Card.Body>
                    <div className="d-flex flex-column align-items-center justify-content-center text-center">
                        <Card.Title className="mb-0 text-dark fw-bold" style={{ fontSize: '1.5rem' }}>
                            {year.ano}
                        </Card.Title>
                        {year.status === 'VIGENTE' && (
                            <Card.Subtitle className="text-muted mt-1">(Ano atual)</Card.Subtitle>
                        )}
                    </div>
                </Card.Body>
            </Card>
        </Col>
    );

    if (loading || yearLoading) {
        return (
            <Container className="mt-4 text-center">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Carregando...</span>
                </Spinner>
            </Container>
        );
    }

    if (error) {
        return (
            <Container className="mt-4">
                <Alert variant="danger" onClose={() => setError(null)} dismissible>
                    {error}
                </Alert>
                {!currentYear && availableYears.length > 0 && (
                    <Row className="g-4 mt-3">
                        {availableYears.map(renderYearCard)}
                    </Row>
                )}
            </Container>
        );
    }

    if (!currentYear) {
        return (
            <Container className="mt-4">
                <div className="mb-5">
                    <h5 className="mb-3">Anos Disponíveis</h5>
                    <Row className="g-4">
                        {availableYears.map(renderYearCard)}
                    </Row>
                </div>
            </Container>
        );
    }

    return (
        <Container className="mt-4 text-center">
            <div className="py-5">
                <h4>Ano selecionado: {currentYear.ano}</h4>
                <p className="text-muted">Aguarde, redirecionando...</p>
                <Spinner animation="border" role="status" className="mt-3">
                    <span className="visually-hidden">Redirecionando...</span>
                </Spinner>
            </div>
        </Container>
    );
};

export default YearSelectionComponent;
